// src/pages/materias/laboral/LaboralProcedimientoPage/useLaboralCatalogos.js

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { actionEstadosGet } from "../../../../redux/actions/estados/estados";
import { actionCiudadesGet } from "../../../../redux/actions/ciudades/ciudades";
import { actionEmpresasGet } from "../../../../redux/actions/empresas/empresas";
import { PATH } from "../../../../redux/utils";

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Cuando se pasa `modulo` (conciliacion/tribunal/junta/desvinculaciones), las
// ciudades ya NO salen del slice global `state.ciudades` (filtrado por el
// mecanismo legacy de users_despachos), sino de GET /ciudades?modulo=X, que
// usa el permiso de ciudades de ese modulo (Usuarios > Permisos, ver
// usuarios_privilegios_ciudades). Se pide aparte, en estado local, para no
// pisar el slice compartido que otras pantallas (sin `modulo`) siguen usando.
export default function useLaboralCatalogos(selectedEstadoId, selectedCiudadId, opts = {}) {
  const { modulo } = opts;
  const dispatch = useDispatch();

  const estadosSlice = useSelector((state) => state.estados || {});
  const ciudadesSliceGlobal = useSelector((state) => state.ciudades || {});
  const empresasSlice = useSelector((state) => state.empresas || {});

  const [ciudadesModulo, setCiudadesModulo] = useState(null);

useEffect(() => {
  const estadosHave = Array.isArray(estadosSlice?.data?.items) && estadosSlice.data.items.length > 0;
  const empresasHave = Array.isArray(empresasSlice?.data?.items) && empresasSlice.data.items.length > 0;

  if (!estadosHave) dispatch(actionEstadosGet({}));
  if (!empresasHave) dispatch(actionEmpresasGet({}));

  if (!modulo) {
    const ciudadesHave = Array.isArray(ciudadesSliceGlobal?.data?.items) && ciudadesSliceGlobal.data.items.length > 0;
    if (!ciudadesHave) dispatch(actionCiudadesGet({}));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [dispatch, modulo]);

  useEffect(() => {
    if (!modulo) return;
    let cancelled = false;
    setCiudadesModulo(null);
    axios
      .get(`${PATH}/ciudades`, { params: { modulo }, headers: authHeader() })
      .then(({ data }) => {
        if (!cancelled) setCiudadesModulo(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setCiudadesModulo([]);
      });
    return () => {
      cancelled = true;
    };
  }, [modulo]);

  const estadosItems = useMemo(
    () => estadosSlice?.data?.items || [],
    [estadosSlice?.data]
  );

  const ciudadesItems = useMemo(() => {
    if (modulo) return Array.isArray(ciudadesModulo) ? ciudadesModulo : [];
    return ciudadesSliceGlobal?.data?.items || [];
  }, [modulo, ciudadesModulo, ciudadesSliceGlobal?.data]);

  const empresasItems = useMemo(
    () => empresasSlice?.data?.items || [],
    [empresasSlice?.data]
  );

  const estadosOptions = useMemo(
    () =>
      estadosItems.map((e) => ({
        label: e.nombre ?? e.code ?? `Estado ${e.id}`,
        value: e.id,
      })),
    [estadosItems]
  );

  const ciudadesOptions = useMemo(() => {
    if (!selectedEstadoId) return [];
    return ciudadesItems
      .filter((c) => String(c.id_estado) === String(selectedEstadoId))
      .map((c) => ({
        label: c.nombre ?? c.code ?? `Ciudad ${c.id}`,
        value: c.id,
      }));
  }, [ciudadesItems, selectedEstadoId]);

  const empresasOptions = useMemo(
    () =>
      empresasItems.map((e) => ({
        label: e.nombre || e.razon_social || e.alias || `Empresa ${e.id}`,
        value: e.id,
      })),
    [empresasItems]
  );

  const estadoSeleccionado = useMemo(
    () =>
      estadosItems.find((e) => String(e.id) === String(selectedEstadoId)) || null,
    [estadosItems, selectedEstadoId]
  );

  const ciudadSeleccionada = useMemo(
    () =>
      ciudadesItems.find((c) => String(c.id) === String(selectedCiudadId)) || null,
    [ciudadesItems, selectedCiudadId]
  );

  const estadosById = useMemo(() => {
    return estadosItems.reduce((acc, e) => {
      acc[e.id] = e;
      return acc;
    }, {});
  }, [estadosItems]);

  const ciudadesById = useMemo(() => {
    return ciudadesItems.reduce((acc, c) => {
      acc[c.id] = c;
      return acc;
    }, {});
  }, [ciudadesItems]);

  return {
    estadosOptions,
    ciudadesOptions,
    empresasOptions,
    estadoSeleccionado,
    ciudadSeleccionada,
    estadosById,
    ciudadesById,
  };
}
